import React from 'react';
import { useLocation } from 'react-router-dom';
import AdRenderer from '@/components/AdRenderer';

/**
 * Global Ad Provider Component
 * 
 * This component renders ads globally across all pages based on campaign settings.
 * It listens to route changes and fetches appropriate ads for each page.
 * 
 * Supported global zones:
 * - header_top: Banner at the very top of the page
 * - header_below_nav: Banner below navigation
 * - footer_top: Banner above footer
 * - modal_popup: Popup ads (shown with delay)
 * - exit_intent: Shown when user tries to leave
 * - slide_in: Slide-in notification style ads
 * - toast: Toast notification style ads
 */
const GlobalAdProvider = ({ children }) => {
  const location = useLocation();
  const pagePath = location.pathname;

  // Pages where ads should NOT be shown
  const excludedPages = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/checkout',
    '/admin/advertisements', // Don't show ads on ad management page
  ];

  const shouldShowAds = !excludedPages.some(page => 
    pagePath === page || pagePath.startsWith(page + '/')
  );

  return (
    <>
      {/* Global Header Ad - shown at the top of every page */}
      {shouldShowAds && (
        <div className="global-ad-header">
          <AdRenderer 
            zone="header_top" 
            pagePath={pagePath}
            trigger="page_load"
            className="mb-0"
          />
        </div>
      )}

      {/* Main page content */}
      {children}

      {/* Global Footer Ad - shown at the bottom of every page */}
      {shouldShowAds && (
        <div className="global-ad-footer">
          <AdRenderer 
            zone="footer_top" 
            pagePath={pagePath}
            trigger="page_load"
            className="mt-4"
          />
        </div>
      )}

      {/* Modal/Popup ads - shown with delay */}
      {shouldShowAds && (
        <AdRenderer 
          zone="modal_popup" 
          pagePath={pagePath}
          trigger="after_delay"
        />
      )}

      {/* Exit intent ads - shown when user tries to leave */}
      {shouldShowAds && (
        <AdRenderer 
          zone="exit_intent" 
          pagePath={pagePath}
          trigger="exit_intent"
        />
      )}

      {/* Slide-in ads - shown after scroll */}
      {shouldShowAds && (
        <AdRenderer 
          zone="slide_in" 
          pagePath={pagePath}
          trigger="on_scroll"
        />
      )}
    </>
  );
};

export default GlobalAdProvider;
